import styled, { useTheme } from 'styled-components';
import { ClientSummary } from '../../../2_domain/client/client';
import { Staff } from '../../../2_domain/staff/staff';
import { TestItemSummary } from '../../../2_domain/test/test';
import { toIDPrice } from '../../../lib/util/numberFormatter';
import Column from '../../core/components/box/column';
import LXInformationCard from '../../core/components/box/lxInformationCard';
import Row from '../../core/components/box/row';
import SizedBox from '../../core/components/box/sizedBox';
import LXLargeButton from '../../core/components/buttons/lxLargeButton';
import { LXbody, LXCaption, LXh4 } from '../../core/texts/lxText';

interface TestAddOverviewProps {
  onPrev: () => void;
  onSave: () => Promise<void>;
  client: ClientSummary;
  testItems: TestItemSummary[];
  receptionist: Staff;
  attendingPhysician: Staff;
  memo: Memo;
}

const TestAddOverView = (props: TestAddOverviewProps) => {
  const theme = useTheme();

  const header = (
    <Row justifyContent="space-between">
      <Column>
        <LXh4>{theme.texts.confirm}</LXh4>
        <SizedBox height={4}></SizedBox>
        <LXbody color={theme.colors.caption}>
          {theme.texts.pleaseCheckTheInformationYouEntered}
        </LXbody>
      </Column>
      <Row>
        <LXLargeButton onClick={async () => props.onPrev()} kind={'secondary'}>
          {theme.texts.prev}
        </LXLargeButton>
        <SizedBox width={8}></SizedBox>
        <LXLargeButton onClick={async () => props.onSave()}>
          {theme.test.orderComplete}
        </LXLargeButton>
      </Row>
    </Row>
  );

  const buildOverview = () => {
    const left = new Map<string, string>();
    const right = new Map<string, string>();
    const totalPrice = props.testItems.reduce(
      (acc, cur) => acc + cur.amount,
      0
    );

    left.set(theme.texts.client, props.client.name);
    left.set(theme.texts.clientNo, `#${props.client.clientSerial}`);
    left.set(theme.texts.totalPrice, toIDPrice(totalPrice, true));
    left.set(
      theme.staff.receptionist,
      `${props.receptionist.name} (${props.receptionist.phone})`
    );
    left.set(
      theme.staff.attendingPhysician,
      `${props.attendingPhysician.name} (${props.attendingPhysician.phone})`
    );
    right.set(theme.texts.memo, props.memo);

    return <LXInformationCard left={left} right={right}></LXInformationCard>;
  };

  return (
    <Column>
      {header}
      <SizedBox height={40}></SizedBox>
      <LXbody color={theme.colors.caption}>{theme.texts.testItem}</LXbody>
      <SizedBox height={16}></SizedBox>
      <TestItemOverviewList testItems={props.testItems}></TestItemOverviewList>
      <SizedBox height={40}></SizedBox>
      {buildOverview()}
    </Column>
  );
};

interface TestItemOverviewListProps {
  testItems: TestItemSummary[];
}

const TestItemOverviewListContainer = styled.div`
  width: 900px;
  border-radius: 12px;
  border: ${(props) => `1px solid ${props.theme.colors.grayLine}`};
`;

const TestItemOverviewListHeaderContainer = styled.div`
  width: 100%;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  background-color: ${(props) => props.theme.colors.grayBgLight};
  padding: 12px 30px;
  display: flex;
  flex-direction: row;
`;

const TestItemOverviewListItemContainer = styled.div`
  width: 100%;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  padding: 0px 30px 20px 30px;
  display: flex;
  flex-direction: row;
`;

const TestItemOverviewList = (props: TestItemOverviewListProps) => {
  const theme = useTheme();

  const header = (
    <TestItemOverviewListHeaderContainer>
      <LXCaption width={'40px'}>{theme.texts.no}</LXCaption>
      <SizedBox width={16}></SizedBox>
      <LXCaption width={'80px'}>{theme.texts.testCode}</LXCaption>
      <SizedBox width={16}></SizedBox>
      <LXCaption width={'404px'}>{theme.texts.testName}</LXCaption>
      <SizedBox width={16}></SizedBox>
      <LXCaption width={'160px'}>{theme.texts.price}</LXCaption>
      <SizedBox width={16}></SizedBox>
      <LXCaption>{theme.texts.requiredDay}</LXCaption>
    </TestItemOverviewListHeaderContainer>
  );

  const buildItems = () => {
    return props.testItems.map((item, idx) => (
      <TestItemOverviewListItemContainer key={item.id}>
        <LXbody width={'40px'}>{idx + 1}</LXbody>
        <SizedBox width={16}></SizedBox>
        <LXbody width={'80px'}>{item.serial}</LXbody>
        <SizedBox width={16}></SizedBox>
        <LXbody width={'404px'}>{item.name}</LXbody>
        <SizedBox width={16}></SizedBox>
        <LXbody width={'160px'}>{toIDPrice(item.amount, true)}</LXbody>
        <SizedBox width={16}></SizedBox>
        <LXbody>{`${item.requiredDays} days`}</LXbody>
      </TestItemOverviewListItemContainer>
    ));
  };

  return (
    <TestItemOverviewListContainer>
      <Column>
        {header}
        <SizedBox height={20}></SizedBox>
        {buildItems()}
      </Column>
    </TestItemOverviewListContainer>
  );
};

export default TestAddOverView;
